import { http } from '@pendo/http';
import isEmpty from 'lodash/isEmpty';
import { hasReasoningSummaries } from '@/ask-pendo/utils/message';
import { hasSegmentFlag } from '@/data/flags/local-flags';
import { v4 as uuid } from 'uuid';

function buildUrlWithParams (baseUrl, options = {}) {
    const allowedParams = ['revision'];
    const params = new URLSearchParams();

    allowedParams.forEach((param) => {
        if (options[param] !== undefined && options[param] !== null) {
            params.set(param, options[param]);
        }
    });

    return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
}

export function fetchUserConversations () {
    return http.get('/api/s/_SID_/ask-pendo/conversations');
}

export function fetchConversation (conversationId) {
    return http.get(`/api/s/_SID_/ask-pendo/conversations/${conversationId}`);
}

export function postAskPendoChat (conversation, employeeConfig, options = {}, userRole = '', customInstructions = '') {
    const route = hasSegmentFlag('leoSupervisor') ? 'supervisor' : 'chat';
    const url = buildUrlWithParams(`/api/streaming/s/_SID_/ask-pendo/${route}`, options);

    const payload = {
        conversation,
    };

    if (!isEmpty(employeeConfig)) {
        payload.employeeConfig = employeeConfig;
    }

    if (userRole) {
        payload.userRole = userRole;
    }

    if (customInstructions) {
        payload.customInstructions = customInstructions;
    }

    const requestOptions = {
        responseType: 'stream',
        adapter: 'fetch',
    };

    // Pass through AbortController signal if provided
    if (options.signal) {
        requestOptions.signal = options.signal;
    }

    return http.post(url, payload, requestOptions).then((res) => res.data);
}

export function postAskPendoGuides (conversation) {
    return http.post('/api/s/_SID_/ask-pendo/guides', { conversation }).then((res) => res.data);
}

export function putAskPendoConversation (conversation = {}) {
    const { conversationId } = conversation;
    if (!conversationId) return Promise.reject(new Error('Conversation ID is required'));

    return http.put(`/api/s/_SID_/ask-pendo/conversations/${conversationId}`, conversation ).then((res) => res.data);
}

export function postAskPendoCustomer (conversation, _employeeConfig = {}, options = {}) {
    const route = hasSegmentFlag('multiAgentCF') ? 'supervisor' : 'customer-finder';
    const url = buildUrlWithParams(`/api/streaming/s/_SID_/ask-pendo/${route}`, options);

    const requestOptions = {
        responseType: 'stream',
        adapter: 'fetch',
    };

    // Pass through AbortController signal if provided
    if (options.signal) {
        requestOptions.signal = options.signal;
    }

    return http
        .post(
            url,
            {
                conversation,
            },
            requestOptions,
        )
        .then((res) => res.data);
}

export function postAskPendoListen (conversation, _employeeConfig = {}, options = {}) {
    const url = buildUrlWithParams('/api/streaming/s/_SID_/ask-pendo/listen', options);

    const requestOptions = {
        responseType: 'stream',
        adapter: 'fetch',
    };

    // Pass through AbortController signal if provided
    if (options.signal) {
        requestOptions.signal = options.signal;
    }

    return http
        .post(
            url,
            {
                conversation,
            },
            requestOptions,
        )
        .then((res) => res.data);
}

export function postClaudeAgent (conversation, employeeConfig = {}, options = {}, userRole = '', customInstructions = '') {
    const url = buildUrlWithParams('/api/streaming/s/_SID_/claude-agent/query', options);

    const payload = {
        conversation,
        config: {},
    };

    if (!isEmpty(employeeConfig)) {
        payload.config.employeeConfig = employeeConfig;
    }

    if (userRole) {
        payload.config.userRole = userRole;
    }

    if (customInstructions) {
        payload.config.customInstructions = customInstructions;
    }

    const requestOptions = {
        responseType: 'stream',
        adapter: 'fetch',
    };

    if (options.signal) {
        requestOptions.signal = options.signal;
    }

    return http.post(url, payload, requestOptions).then((res) => res.data);
}

export function fetchClaudeAgentFile (conversationId, filepath) {
    return http.get(`/api/s/_SID_/claude-agent/files/${conversationId}/${filepath}`);
}

export function getAskPendoPostRequest (type) {
    switch (type) {
        case 'guides':
            return (conversation, _employeeConfig = {}, options) => postAskPendoGuides(conversation, options);
        case 'chat':
            return (conversation, employeeConfig, options, userRole, customInstructions) =>
                postAskPendoChat(conversation, employeeConfig, options, userRole, customInstructions);
        case 'customer':
            return (conversation, employeeConfig, options) =>
                postAskPendoCustomer(conversation, employeeConfig, options);
        case 'listen':
            return (conversation, employeeConfig, options) => postAskPendoListen(conversation, employeeConfig, options);
        case 'agent':
            return (conversation, employeeConfig, options, userRole, customInstructions) =>
                postClaudeAgent(conversation, employeeConfig, options, userRole, customInstructions);
        default:
            return undefined;
    }
}

export function getInitialState () {
    return {
        userConversations: [],
        conversations: {},
        pendingResponses: {}, // Track which conversations are waiting for assistant response
        activeStreamIds: {}, // Track the currently active stream ID for each conversation
        abortControllers: {}, // Track AbortController instances per conversation
        employeeConfig: {},
        customInstructions: '', // Track custom instructions for the user
        configDrawerOpen: false, // Track whether the config drawer is open
        artifactConfig: {}, // Track local changes to artifact configurations for each artifact
        artifactMenuItems: {}, // Track dropdown menu items associated with each artifact
    };
}

const isBlank = (item) => !item || item.trim() === '';

export function parseMessageContent (message) {
    let content = '';
    let type = null;
    let reasoningSummaries = null;

    if (typeof message.content === 'string') {
        type = 'text';
        content = message.content;
    }

    if (Array.isArray(message.content)) {
        const textContent = message.content.find((el) => el.type === 'text');

        if (textContent) {
            type = 'text';
            content = textContent.text;
        }

        if (hasReasoningSummaries(message)) {
            const reasoningContent = message.content.find((el) => el.type === 'reasoning');
            reasoningSummaries = reasoningContent.summary;
        }
    }

    return { id: message.id, type, content, reasoningSummaries };
}

export function cleanupIncompleteStoppedMessages (messages) {
    for (let i = 0; i < messages.length - 1; i++) {
        const a = messages[i];
        const b = messages[i + 1];
        const aStopped =
            a &&
            a.type === 'assistant' &&
            parseMessageContent(a).content === 'Request was stopped by the user.' &&
            !a.tool_calls;
        const bRealAssistant =
            b &&
            b.type === 'assistant' &&
            !isBlank(parseMessageContent(b).content) &&
            parseMessageContent(b).content !== 'Request was stopped by the user.' &&
            !b.tool_calls;

        if (aStopped && bRealAssistant) {
            messages.splice(i, 1);
            i--;
        }
    }
}

export function appendGuideNextActionArtifact (conversation, $t, { segment }) {
    const content = $t('askPendo.guide.Segment was created');
    const name = $t('askPendo.guide.Create guide');
    const artifactId = uuid();
    const nextActionGuideArtifact = {
        data: {
            segment_id: segment.id,
        },
        artifact_id: artifactId,
        id: uuid(),
        name,
        action_type: 'create_guide',
        type: 'next_actions',
    };

    const nextActionsGuideMessage = {
        content,
        id: uuid(),
        type: 'assistant',
        metadata: {
            artifact_ids: [artifactId],
        },
    };

    if (!conversation.artifacts) {
        conversation.artifacts = {};
    }

    conversation.artifacts[artifactId] = nextActionGuideArtifact;
    conversation.messages.push(nextActionsGuideMessage);
    conversation.last_artifact_id = artifactId;

    return conversation;
}
